<template>
<div class="site container hfeed inner-page">
  <HeadNav selectedLanguage="en" page="blogs" />
  <div class="site-content page-primary">
    <article class="article-shell page-main-narrow" v-if="article">
      <Breadcrumbs :items="crumbs" />
      <p class="article-date">{{ article.date }}</p>
      <h1>{{ article.title }}</h1>

      <section v-for="(section, index) in article.sections" :key="section.heading || section.body[0]" class="article-section">
        <h2 v-if="section.heading">{{ section.heading }}</h2>
        <template v-for="(paragraph, paragraphIndex) in section.body">
          <p :key="paragraphKey(paragraph, paragraphIndex)">
            <template v-for="(segment, segmentIndex) in paragraphSegments(paragraph)">
              <router-link
                v-if="segment.to"
                :key="`${paragraphKey(paragraph, paragraphIndex)}-${segmentIndex}`"
                class="article-link"
                :to="segment.to"
              >
                <strong v-if="segment.strong">{{ segment.text }}</strong>
                <span v-else>{{ segment.text }}</span>
              </router-link>
              <strong
                v-else-if="segment.strong"
                :key="`${paragraphKey(paragraph, paragraphIndex)}-${segmentIndex}`"
              >
                {{ segment.text }}
              </strong>
              <span v-else :key="`${paragraphKey(paragraph, paragraphIndex)}-${segmentIndex}`">{{ segment.text }}</span>
            </template>
          </p>
          <div
            v-if="section.table && section.table.afterParagraphIndex === paragraphIndex"
            :key="`${section.heading}-table-${paragraphIndex}`"
            class="article-inline-table"
          >
            <table>
              <thead>
                <tr>
                  <th v-for="heading in section.table.headers" :key="heading" scope="col">{{ heading }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in section.table.rows" :key="row.join('-')">
                  <td v-for="cell in row" :key="cell">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
        <ul v-if="section.list">
          <li v-for="item in section.list" :key="item">{{ item }}</li>
        </ul>
        <p v-if="section.after">{{ section.after }}</p>
        <img
          v-if="index === 0"
          class="article-poster"
          :src="article.poster"
          :alt="article.posterAlt || article.title"
          loading="lazy"
          decoding="async"
        >
      </section>

      <h2>FAQ</h2>
      <div v-for="item in article.faq" :key="item.q" class="faq-item">
        <h3>{{ item.q }}</h3>
        <p>{{ item.a }}</p>
      </div>
    </article>
  </div>
</div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import '@/css/page-narrow-layout.scss';
import HeadNav from '@/components/HeadNav.vue';
import Breadcrumbs from '@/components/Breadcrumbs.vue';
import { articles } from '@/documents/blogs.js';

export default {
  name: 'BlogArticle',
  components: { HeadNav, Breadcrumbs },
  computed: {
    article() {
      return articles.find((item) => item.slug === this.$route.meta.articleSlug);
    },
    crumbs() {
      return [
        { label: 'Home', to: '/' },
        { label: 'Blog', to: '/blog/' },
        { label: this.article ? this.article.title : 'Article', to: '' },
      ];
    },
  },
  methods: {
    paragraphKey(paragraph, index) {
      return typeof paragraph === 'string' ? paragraph : `${paragraph.text}-${index}`;
    },
    paragraphText(paragraph) {
      return typeof paragraph === 'string' ? paragraph : paragraph.text;
    },
    paragraphSegments(paragraph) {
      const text = this.paragraphText(paragraph);
      const marks = typeof paragraph === 'string' ? [] : paragraph.marks || [];
      const segments = [];
      let cursor = 0;

      while (cursor < text.length) {
        let next = null;

        marks.forEach((mark) => {
          const start = text.indexOf(mark.text, cursor);
          if (start < 0) return;
          if (!next || start < next.start) {
            next = { ...mark, start };
          }
        });

        if (!next) {
          segments.push({ text: text.slice(cursor) });
          break;
        }

        if (next.start > cursor) {
          segments.push({ text: text.slice(cursor, next.start) });
        }

        segments.push({
          text: next.text,
          to: next.to,
          strong: next.strong,
        });
        cursor = next.start + next.text.length;
      }

      return segments;
    },
  },
};
</script>

<style lang="scss" scoped>
.inner-page .article-shell {
  width: 85%;
  margin: 0 auto;
  padding: 1rem 1.25rem 3.5rem;
  line-height: 1.7;
  font-size: 1.05rem;
  color: #222;
}
.article-poster {
  display: block;
  width: min(520px, 100%);
  max-height: 420px;
  margin: 1rem auto 1.5rem;
  border-radius: 8px;
  object-fit: contain;
  background: #f7faf9;
}
h1 {
  color: #128c7e;
  font-size: 2rem;
  line-height: 1.2;
  text-align: center;
}
h2 {
  color: #075e54;
  font-size: 1.35rem;
  margin-top: 2rem;
}
h3 {
  color: #222;
  font-size: 1.08rem;
  margin-bottom: 0.25rem;
}
.article-date {
  color: #666;
  font-size: 0.95rem;
  text-align: center;
}
.article-link {
  color: #2563eb;
  font-weight: 700;
  text-decoration: none;
}
.article-link:hover {
  text-decoration: underline;
}
.article-inline-table {
  display: flex;
  justify-content: flex-start;
  margin: 0.8rem 0 1.4rem;
  overflow-x: auto;
}
.article-inline-table table {
  width: 280px;
  max-width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  line-height: 1.25;
}
.article-inline-table th,
.article-inline-table td {
  padding: 0.35rem 0.45rem;
  text-align: left;
  vertical-align: top;
  border: 1px solid #cfcfcf;
}
.article-inline-table th {
  color: #111;
  font-weight: 700;
  background: #fff;
}
ul {
  padding-left: 1.35rem;
}
li {
  margin: 0.35rem 0;
}
.faq-item {
  margin-bottom: 1rem;
}
</style>
